// Main-thread half of automatic alignment: fetch each slide as a raster, bring
// the two to a COMMON physical scale, hand them to the worker, and convert what
// comes back into the transform the viewer speaks.
//
// The common-scale step is the one that makes the whole thing tractable. Both
// slides carry PixelSpacing, so resampling them to the same mm-per-pixel leaves
// the residual scale within a few percent of 1 — which turns a four-parameter
// search into a one-parameter one (see align.worker.ts).

import type { SlideRaster, ViewportEngine } from '../ViewportEngine';
import type { SimilarityTransform } from './transform';
import type { AlignRequest, AlignResult, RasterMsg } from './align.worker';

/** Long edge, in pixels, of the two working resolutions. Coarse finds the
 *  rotation; fine refines on structure, which needs the detail. */
const COARSE_PX = 256;
const FINE_PX = 512;

export interface AutoAlignOutcome {
  ok: boolean;
  reason?: string;
  /** A → B in base-level display pixels, ready for the compare view. */
  transform?: SimilarityTransform;
  confidence?: 'high' | 'medium' | 'low';
  dice?: number;
  /** Correlation of gradient structure at the finer scale; 0 if that stage was
   *  skipped. Useful for telling "the outlines agree" from "the tissue agrees". */
  ncc?: number;
  /** Winning rotation's score over the best rival more than 20° away. */
  ambiguity?: number;
  ms?: number;
}

/** Draw a raster into a canvas of the given size and read the pixels back. The
 *  browser's own downscaling is both better and faster than anything worth
 *  writing here. */
function resample(r: SlideRaster, w: number, h: number): RasterMsg {
  const src = document.createElement('canvas');
  src.width = r.width;
  src.height = r.height;
  const sctx = src.getContext('2d')!;
  // Copy through a fresh buffer: ImageData insists on a plain ArrayBuffer, and
  // the raster's may be shared.
  const img = sctx.createImageData(r.width, r.height);
  img.data.set(r.data);
  sctx.putImageData(img, 0, 0);

  const dst = document.createElement('canvas');
  dst.width = w;
  dst.height = h;
  const dctx = dst.getContext('2d')!;
  dctx.imageSmoothingQuality = 'high';
  dctx.drawImage(src, 0, 0, w, h);
  return { data: dctx.getImageData(0, 0, w, h).data, width: w, height: h };
}

export function alignSlides(
  engines: [ViewportEngine | null, ViewportEngine | null],
  signal?: { cancelled: boolean },
): Promise<AutoAlignOutcome> {
  return (async () => {
    const [ea, eb] = engines;
    if (!ea || !eb) return { ok: false, reason: 'Both panes need a slide.' };

    const [ra, rb] = await Promise.all([
      ea.slideRaster().catch(() => null),
      eb.slideRaster().catch(() => null),
    ]);
    // Name the pane. "one of the slides" leaves nowhere to look, and the usual
    // cause — a page held open across a change to the study, so the series it
    // remembers no longer exist — is only obvious once you know which side failed.
    if (!ra || !rb) {
      const which = !ra && !rb ? 'either slide' : !ra ? 'slide A' : 'slide B';
      return { ok: false, reason: `Could not read ${which}. Try reloading the page.` };
    }
    if (signal?.cancelled) return { ok: false, reason: 'cancelled' };

    const ga = ea.getViewGeometry();
    const gb = eb.getViewGeometry();
    if (!ga || !gb) return { ok: false, reason: 'Slide geometry unavailable.' };

    // How big each slide is, in whatever unit both can be expressed in.
    //
    // Millimetres when BOTH report a real pixel spacing. If either is synthetic
    // the pair falls back together to base-pixel extent — mixing one slide's
    // millimetres with a stand-in for the other is far worse than using a
    // consistent proxy for both, since the two would be resampled to sizes with
    // no relation to each other (that produced a scale estimate of 0.13 against
    // a true 1.5). The fallback assumes the slides cover comparable glass, and
    // the worker is told to widen its scale search to cover being wrong.
    const measured = ra.mmPerPx != null && rb.mmPerPx != null;
    const physA = measured
      ? { w: ra.width * ra.mmPerPx!, h: ra.height * ra.mmPerPx! }
      : { w: ga.imageWidth, h: ga.imageHeight };
    const physB = measured
      ? { w: rb.width * rb.mmPerPx!, h: rb.height * rb.mmPerPx! }
      : { w: gb.imageWidth, h: gb.imageHeight };

    const build = (target: number) => {
      // One mm-per-pixel for both, sized so the larger slide hits the target.
      const mmPerPx = Math.max(
        Math.max(physA.w, physA.h) / target,
        Math.max(physB.w, physB.h) / target,
      );
      const dims = (p: { w: number; h: number }) => ({
        w: Math.max(16, Math.round(p.w / mmPerPx)),
        h: Math.max(16, Math.round(p.h / mmPerPx)),
      });
      const da = dims(physA);
      const db = dims(physB);
      return { a: resample(ra, da.w, da.h), b: resample(rb, db.w, db.h) };
    };

    const coarse = build(COARSE_PX);
    const fine = build(FINE_PX);
    if (signal?.cancelled) return { ok: false, reason: 'cancelled' };

    const result = await runWorker({
      coarse,
      fine,
      scalePrior: 1,
      scaleUncertain: !measured,
    });
    if (signal?.cancelled) return { ok: false, reason: 'cancelled' };
    if (!result.ok || !result.transform) {
      return { ok: false, reason: result.reason ?? 'No alignment found.' };
    }

    // The worker works in coarse-raster pixels. Convert to base display pixels:
    // p_coarse = k * p_base for each slide, so scale picks up kA/kB and the
    // translation divides by kB.
    const kA = coarse.a.width / ga.imageWidth;
    const kB = coarse.b.width / gb.imageWidth;
    const t = result.transform;
    return {
      ok: true,
      transform: {
        scale: (t.scale * kA) / kB,
        rotationDeg: (t.rot * 180) / Math.PI,
        tx: t.tx / kB,
        ty: t.ty / kB,
      },
      confidence: result.confidence,
      dice: result.dice,
      ncc: result.ncc,
      ambiguity: result.ambiguity,
      ms: result.ms,
    };
  })();
}

function runWorker(req: AlignRequest): Promise<AlignResult> {
  return new Promise((resolve) => {
    const worker = new Worker(new URL('./align.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<AlignResult>) => {
      resolve(e.data);
      worker.terminate();
    };
    worker.onerror = (e) => {
      resolve({ ok: false, reason: e.message || 'Alignment failed.' });
      worker.terminate();
    };
    worker.postMessage(req, [req.coarse.a.data.buffer, req.coarse.b.data.buffer,
      req.fine.a.data.buffer, req.fine.b.data.buffer]);
  });
}
